import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Play, CheckCircle, Clock, Trophy, BookOpen } from "lucide-react";
import PhoneFrame from "../components/PhoneFrame.jsx";
import api from "../lib/api.js";

const CATEGORY_LABELS = {
  walking: "Walking",
  posture: "Posture",
  breathing: "Breathing",
  mindfulness: "Mindfulness",
  recovery: "Recovery",
  stretching: "Stretching",
  corporate: "Corporate Wellness",
};

const CATEGORY_COLORS = {
  walking: "#2fbf71",
  posture: "#00B4D8",
  breathing: "#a78bfa",
  mindfulness: "#a78bfa",
  recovery: "#F59E0B",
  stretching: "#84cc16",
  corporate: "#94A3B8",
};

export default function Library() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/content").then((r) => setItems(r.data));
  }, []);

  const categories = ["all", ...Array.from(new Set(items.map((i) => i.category)))];
  const filtered = filter === "all" ? items : items.filter((i) => i.category === filter);

  return (
    <PhoneFrame>
      <div className="px-5 pt-6 pb-28">
        <button onClick={() => nav(-1)} className="text-slate-400 inline-flex items-center gap-1 text-sm" data-testid="library-back">
          <ChevronLeft size={16} /> Back
        </button>
        <div className="mt-4 flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-[#142534] flex items-center justify-center">
            <BookOpen size={20} color="#2fbf71" />
          </div>
          <div>
            <h1 className="ps-heading text-2xl font-bold">Content Library</h1>
            <p className="text-slate-400 text-sm">Bite-size lessons that earn points.</p>
          </div>
        </div>

        <div className="mt-5 flex gap-2 overflow-x-auto no-scrollbar pb-1">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`library-filter-${c}`}
              onClick={() => setFilter(c)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border ${
                filter === c
                  ? "bg-[#2fbf71] text-[#0a1822] border-transparent"
                  : "border-[#1a2f40] text-slate-300"
              }`}
            >
              {c === "all" ? "All" : CATEGORY_LABELS[c] || c}
            </button>
          ))}
        </div>

        <div className="mt-5 space-y-3">
          {filtered.map((item) => {
            const color = CATEGORY_COLORS[item.category] || "#2fbf71";
            return (
              <Link
                key={item.id}
                to={`/library/${item.id}`}
                data-testid={`library-item-${item.id}`}
                className="ps-card p-4 flex gap-3 items-center hover:border-[#2fbf71]/40"
              >
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${color}22`, boxShadow: `0 0 0 1px ${color}44` }}>
                  {item.completed ? <CheckCircle size={20} color={color} /> : <Play size={20} color={color} fill={color} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color }}>
                    {CATEGORY_LABELS[item.category] || item.category}
                  </div>
                  <div className="font-semibold text-sm mt-0.5 truncate">{item.title}</div>
                  <div className="text-xs text-slate-400 mt-1 flex items-center gap-3">
                    <span className="inline-flex items-center gap-1"><Clock size={11} /> {item.duration_min} min</span>
                    <span className="inline-flex items-center gap-1"><Trophy size={11} color="#F59E0B" /> {item.points_reward} pts</span>
                    {item.completed && <span className="text-[#2fbf71]">Completed</span>}
                  </div>
                </div>
              </Link>
            );
          })}
          {filtered.length === 0 && (
            <div className="text-slate-500 text-sm text-center py-10">No lessons in this category yet.</div>
          )}
        </div>
      </div>
    </PhoneFrame>
  );
}
